export const WHATSAPP_NUMBER = '17866035862'

export interface Option {
  text: string
  value: number
}

export interface Question {
  id: number
  shortTitle: string
  title: string
  intro: string[]
  subQuestions: string[]
  evaluationQuestion: string
  options: Option[]
}

export const questions: Question[] = [
  {
    id: 1,
    shortTitle: '¿Por qué empezaste?',
    title: '¿Por qué empezaste?',
    intro: [
      'Hubo un momento en que decidiste emprender. Antes de que existieran los clientes actuales, antes del equipo, antes de la operación que tienes hoy.',
      '¿Qué te llevó a ese momento?',
      'No la respuesta política. No la que dirías en una entrevista. La respuesta real.',
      'A veces es una necesidad: independencia económica, salir de un trabajo que no te alimentaba, escapar de una situación difícil. A veces es una visión: construir algo nuevo, resolver un problema que veías y nadie atendía, demostrarte algo a ti mismo. A veces es las dos cosas juntas.',
      'Recordarlo importa porque ese punto de partida sigue operando en ti, aunque no lo veas. Las decisiones que tomas hoy todavía cargan algo de esa motivación original.',
    ],
    subQuestions: [
      '¿Qué situación de vida tenías cuando decidiste emprender?',
      '¿Qué te impulsó realmente — necesidad, visión, escape, oportunidad, otra cosa?',
      '¿Qué creías que el negocio iba a hacer por ti?',
      '¿De esa motivación original, qué sigue presente hoy y qué cambió?',
    ],
    evaluationQuestion:
      '¿Qué tan claro tienes hoy por qué empezaste tu negocio?',
    options: [
      { text: 'Muy claro', value: 2 },
      { text: 'Más o menos', value: 1 },
      { text: 'No lo tengo claro', value: 0 },
    ],
  },
  {
    id: 2,
    shortTitle: '¿Para qué quieres seguir?',
    title: '¿Para qué quieres seguir?',
    intro: [
      'Hoy ya construiste algo. Tienes operación, clientes, equipo, resultados. El "por qué" del comienzo ya hizo su trabajo — te trajo hasta acá.',
      'La pregunta ahora es distinta: ¿para qué quieres seguir?',
      'No tiene que ser la misma respuesta del Paso 1. De hecho, raramente lo es.',
      'La razón por la que empezaste algo no necesariamente es la razón por la que tiene sentido seguir haciéndolo. Lo que necesitabas hace 10 años no es lo que necesitas hoy. Las personas cambian. Los negocios maduran. La pregunta del para qué necesita actualizarse.',
      'A veces esa actualización descubre algo incómodo: que sigues operando por inercia, por miedo a parar, por costumbre. A veces descubre algo profundo: que el negocio sí tiene un para qué claro hoy, solo que nunca lo has articulado en voz alta.',
      'Las dos opciones son útiles. Lo importante es saber dónde estás parado.',
    ],
    subQuestions: [
      'Si tu negocio fuera completamente sustentable sin ti durante un año, ¿qué harías con ese tiempo?',
      '¿Hay aspectos de tu negocio actual que ya no te entusiasman como antes?',
      '¿Estás creciendo el negocio por convicción o por inercia?',
      'Si tuvieras que defender hoy en voz alta por qué tu negocio merece existir — ¿qué dirías?',
    ],
    evaluationQuestion:
      '¿Qué tan claro tienes hoy para qué quieres seguir creciendo tu negocio?',
    options: [
      { text: 'Muy claro', value: 2 },
      { text: 'Más o menos', value: 1 },
      { text: 'No lo tengo claro', value: 0 },
    ],
  },
  {
    id: 3,
    shortTitle: '¿Qué quieres dejar?',
    title: '¿Qué quieres dejar?',
    intro: [
      'Esta es la pregunta que menos dueños se hacen. Pero es la que más decide.',
      '¿Qué quieres dejar?',
      'No solo en términos de herencia financiera. La pregunta es más amplia. ¿Qué tipo de empresa quieres que exista cuando tú ya no estés sosteniéndola personalmente? ¿Qué impacto quieres que haya dejado en las personas que trabajaron contigo, en los clientes que atendiste, en la industria donde operaste?',
      'Esta pregunta importa porque le da dirección a la operación de hoy.',
      'Sin una respuesta clara de qué quieres dejar, las decisiones operativas se vuelven sobre ti — qué te conviene a ti, qué quieres tú, qué necesitas tú. Con una respuesta clara, las decisiones se alinean con algo más grande que tu propio momento.',
      'No es una pregunta sobre cuándo te vas a jubilar. Es una pregunta sobre qué quieres haber construido cuando llegues allí.',
    ],
    subQuestions: [
      'Cuando hablen de tu negocio dentro de 15 años, ¿qué quieres que digan?',
      '¿Hay alguna persona específica que quieres formar/dejar mejor de lo que la encontraste?',
      '¿Qué cambio concreto quieres haber generado en tu industria o tu comunidad?',
      'Si tu negocio existiera 50 años después de ti — ¿qué de ti seguiría presente en él?',
    ],
    evaluationQuestion:
      '¿Qué tan claro tienes hoy qué quieres dejar con tu negocio?',
    options: [
      { text: 'Muy claro', value: 2 },
      { text: 'Más o menos', value: 1 },
      { text: 'No lo tengo claro', value: 0 },
    ],
  },
]

export const highestClarityMessages: Record<number, string> = {
  1: 'Tienes claridad sobre la motivación que te trajo hasta acá. Eso es valioso — es la energía original que sigue alimentando muchas decisiones tuyas, aunque no lo notes. Recordar por qué empezaste es lo que te sostiene en los momentos difíciles. Es el ancla.',
  2: 'Tienes claridad sobre la dirección de hoy. Eso es la pieza más difícil de articular para la mayoría de los dueños — y la más útil. Sabes qué estás defendiendo en cada decisión, qué estás priorizando, qué vale la pena y qué no. Esta claridad es la que vuelve las decisiones operativas mucho más fáciles.',
  3: "Tienes claridad sobre el legado. Eso es raro — y es probablemente lo que más distingue a los dueños que terminan construyendo algo que trasciende. Tu visión del 'qué quieres dejar' es la que protege al negocio de decisiones cortoplacistas y le da sentido a la operación de cada día.",
}

export const lowestClarityMessages: Record<number, string> = {
  1: 'No tienes muy claro hoy por qué empezaste. Eso pasa más de lo que la gente admite — los años de operación cubren la motivación original con capas de obligaciones, costumbres y problemas cotidianos. Recuperar esa claridad no es nostalgia. Es entender qué decisiones tuyas hoy todavía están guiadas por motivaciones que ya no aplican — y cuáles no.',
  2: "No tienes muy claro hoy para qué quieres seguir creciendo. Esta es la pregunta más común sin responder en dueños con años de operación. Y la más costosa. Sin claridad aquí, terminas escalando un negocio que no estás seguro de querer en cinco años, tomando decisiones por inercia que después es difícil deshacer. Articular esta respuesta — incluso si es 'no sé todavía' — es el siguiente nivel de tu trabajo.",
  3: 'No tienes muy claro qué quieres dejar. Es la pregunta menos urgente, pero la más estructural. Sin una respuesta clara aquí, las decisiones operativas se vuelven sobre ti — qué te conviene a ti, qué quieres tú. Con respuesta clara, las decisiones se alinean con algo más grande. Empezar a articularla — aunque sea de forma incompleta — cambia las decisiones de hoy.',
}

export const whatsappMessages: Record<number, string> = {
  1: 'Hola Oscar, hice tu guía de propósito y la pregunta sobre por qué empecé es donde tengo menos claridad. Quiero conversar contigo sobre eso.',
  2: 'Hola Oscar, hice tu guía de propósito y la pregunta sobre para qué quiero seguir es donde tengo menos claridad. Quiero conversar contigo sobre eso.',
  3: 'Hola Oscar, hice tu guía de propósito y la pregunta sobre qué quiero dejar es donde tengo menos claridad. Quiero conversar contigo sobre eso.',
}
